import React, { useContext, useState } from "react";

import SmartForm from '@component/smartForm/SmartForm';
import SmartTextInput from '@component/smartForm/SmartInputText';
import SmartImageUpload from './smartForm/SmartImageUpload';
import SmartRichTextEditor from "./smartForm/SmartRichTextEditor";
import SmartContainer from './smartForm/SmartContainer';

import { usePostClubCreateMutation, usePostSiteCreateMutation } from "@mutation/postMutation";
import { AlertContext } from "@context/AlertProvider";
import { useTranslation } from "react-i18next";
import { removeEmptyValues } from '@utils/generalUtils';
import { createLengthMinValidator, notEmptyValidator } from "@component/smartForm/fieldValidators";

import { ClubSiteTargetObject, SelectClubSiteTarget } from "@component/SelectClubSiteTarget";


function PostCreateForm({ onSave, onCancel }) {
    const postClubCreateMutation = usePostClubCreateMutation();
    const postSiteCreateMutation = usePostSiteCreateMutation();

    const { addAlertBanner } = useContext(AlertContext);
    const { t } = useTranslation(["main", "posts", "error"]);
    const headingValidator = createLengthMinValidator(3);
    const postImageValidator = notEmptyValidator;

    const [postModel, setPostModel] = useState(undefined);
    const [target, setTarget] = useState(new ClubSiteTargetObject(null, null));

    const handleSave = (newModel) => {
        const cleaned = removeEmptyValues({
            heading: newModel.heading,
            body: JSON.stringify(newModel.body),
            file_image_uuid: newModel.file_image.uuid
        });

        const handleMutationError = (error) => {
            if (!error.response) {
            addAlertBanner(t("error:networkError"), "error", 4000);
            return;
            }

            const statusCode = error.response.status;
            const errorDetail = error.response.data?.detail;

            if (statusCode === 403) {
            addAlertBanner(
                target.club_uuid 
                ? t("error:noClubCreationPrivileges") 
                : t("error:noSiteCreationPrivileges"),
                "error",
                5000
            );
            } else if (statusCode === 400) {
            addAlertBanner(
                errorDetail || t("error:creationValidationError"),
                "error",
                5000
            );
            } else if (statusCode === 422) {
            addAlertBanner(
                 t("error:postBodyTooLong"),
                "error",
                5000
            );
            } else if (statusCode === 404) {
            addAlertBanner(
                target.club_uuid
                ? t("error:clubNotFound")
                : t("error:siteNotFound"),
                "error",
                4000
            );
            } else if (statusCode >= 500) {
            addAlertBanner(t("error:serverError"), "error", 4000);
            } else {
            addAlertBanner(t("error:somethingGoesWrong"), "error", 4000);
            }
        };

        // Create new challenge
        if (target.club_uuid) {
            postClubCreateMutation.mutate(
            { data: { ...cleaned, club_uuid: target.club_uuid }},
            {
                onSuccess: () => {
                    setPostModel(undefined);
                    setTarget(new ClubSiteTargetObject(null, null));
                    addAlertBanner(t("main:saveSuccess"), "success", 4000);
                    onSave?.();
                },
                onError: handleMutationError
            }
            );
        } else if (target.site_uuid) {
            postSiteCreateMutation.mutate(
            { data: { ...cleaned, site_uuid: target.site_uuid }},
            {
                onSuccess: () => {
                    setPostModel(undefined);
                    setTarget(new ClubSiteTargetObject(null, null));
                    addAlertBanner(t("main:saveSuccess"), "success", 4000);
                    onSave?.();
                },
                onError: handleMutationError
            }
            );
        } else {
            addAlertBanner(t("error:selectTargetError"), "error", 4000);
        }
    };

    const handleCancel = () => {
        setPostModel(undefined);
        setTarget(new ClubSiteTargetObject(null, null));
        onCancel?.();
    };

    return (
        <>
            <SmartForm
                formModel={postModel}
                setFormModel={setPostModel}
                onSave={handleSave}
                onCancel={handleCancel}
                isCreateOnly={true}
                contentSx={{ gap: 2 }}
                buttonPanelSx={{
                    top: "15px",
                    right: "15px",
                }}
            >
                <SmartContainer sx={{ flex: 1, p: 1 }}>
                    <SmartImageUpload 
                    imageSx={{
                        width: "auto",
                        height: "100%",
                        objectFit: "contain",
                        backgroundColor: "#fff"
                    }}
                    field="file_image.uuid"
                    validator={postImageValidator} />
                </SmartContainer>

                <SmartContainer sx={{ flex: 1, p: 1}} contentSx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <SelectClubSiteTarget 
                        target={target} 
                        setTarget={setTarget} 
                    />
                    <SmartTextInput field="heading" label={t("posts:heading")} validator={headingValidator} />
                    <SmartRichTextEditor field="body"/>
                </SmartContainer>
            </SmartForm>
        </>
    );
}

export default PostCreateForm;
